import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import Badge from "../../../../../components/elements/Badge";
import { taxonomyDelete, taxonomyList, taxonomyStatusChange } from "../../../../../services/taxonomyService";
import DeleteModal from "../../../../../components/common/DeleteModal";
import CategoryMangeDrawer from "../../../../../components/sections/admin/Category/CategoryMangeDrawer";
import Table from "../../../../../components/elements/table/Table";
import Button from "../../../../../components/form/Button";
import { setTitle } from "../../../../../helpers/MetaTag";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { getPaginationRange, handleFilterChange } from "../../../../../helpers";
import TableControls from "../../../../../components/elements/TableControls";
import { featured_filter, status_filter } from "../../../../../components/constant/constant";
import ActionBar from "../../../../../components/elements/ActionBar";
import ConfirmationPopup from "../../../../../components/common/ConfirmationPopup";
import { useModalWithUrl } from "../../../../../hooks/useModalWithUrl";
import StatusModal from "../../../../../components/common/StatusModal";

const JobDepartment = () => {
  setTitle("Job Department");
  const [selectedItems, setSelectedItems] = useState([]);
  const { isOpen, openModal, closeModal } = useModalWithUrl(
      "confirmation",
      "open"
    );
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const userLang = localStorage.getItem('lang');
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, isLoading, updateData, isFetching } = useQuery({
    queryKey: ["department", filters, search],
    queryFn: async () => {
      const res = await taxonomyList({
        ...filters,
        ...(search && { keyword: search }),
        type: "department",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //  status change handler =================>

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["department", filters, search], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                status: item.status === "active" ? "inactive" : "active",
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await taxonomyStatusChange({ ids: [id], status });

      return { res, payload: { id, status } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Job Department status change successfully.");
      } else {
        queryClient.setQueryData(["department", filters, search], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  status: item.status === "active" ? "inactive" : "active",
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const { mutate: featuredChangeHandler, isPending: featuredChangeInPending } = useMutation({
    mutationFn: async ({ id, isFeatured }) => {
      queryClient.setQueryData(["department", filters, search], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                isFeatured: item?.isFeatured === false ? true : false,
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await taxonomyStatusChange({ ids: [id], isFeatured });

      return { res, payload: { id, isFeatured } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Job Department status change successfully.");
      } else {
        queryClient.setQueryData(["department", filters, search], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  isFeatured: item?.isFeatured === false ? true : false,
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Image",
      align: "left",
    },
    {
      _id: 2,
      name: "Title",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-title" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "title" }));
        }
      },
    },

    {
      _id: 3,
      name: "Created At",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
        }
      },
    },

    {
      _id: 4,
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },
    {
      _id: 5,
      name: "Featured",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            isFeatured: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.isFeatured;
            return { ...pre };
          });
        }
      },
      filterData: featured_filter || [],
    },
    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];

  const tagColors = [
    "bg-purple-100 text-purple-700 border border-purple-200",
    "bg-lime-100 text-lime-700 border border-lime-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
    "bg-blue-100 text-blue-700 border border-blue-200",
    "bg-purple-100 text-purple-700 border border-purple-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
    "bg-pink-100 text-pink-700 border border-pink-200",
    "bg-indigo-100 text-indigo-700 border border-indigo-200",
    "bg-teal-100 text-teal-700 border border-teal-200",
    "bg-yellow-100 text-yellow-700 border border-yellow-200",
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "image",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.mediaGallery?.[0]?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.content?.[userLang]?.title || "-",
        },

        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.createdAt ? dayjs(item?.createdAt).format("DD MMM, YYYY") : "-",
        },

        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                statusChangeHandler({
                  id: item?._id,
                  status: item?.status === "active" ? "inactive" : "active",
                });
              }}>
              <Badge
                type={item?.status === "active" ? "success" : "danger"}
                label={item?.status === "active" ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                featuredChangeHandler({
                  id: item?._id,
                  isFeatured: item?.isFeatured === false ? true : false,
                });
              }}>
              <Badge
                type={item?.isFeatured === true ? "info" : "warning"}
                label={item?.isFeatured === true ? "Yes" : "No"}
              />
            </div>
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
            },
            {
              _id: 4,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit Response =================>
  async function taxonomyManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["department", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          await queryClient.refetchQueries(["department", filters, search]);
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }

      if (res?.status === 200) {
        queryClient.setQueryData(["department", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        await queryClient.refetchQueries(["department", filters, search]);
      }

      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  // category delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await taxonomyDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["department", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });
        setSelectedItems([])
        toast.success("Department deleted successfully.");

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["deaprtment", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(error?.message || "Something went wrong while delete department.");
    },
  });

  const options = [
    {
      id: "jobDepartmentDelete",
      icon: "fa-regular fa-trash-can text-red-500",
      tooltip: "Delete Job Departments",
    },
    {
      id: "jobDepartmentStatus",
      icon: "fa-solid fa-eye text-purple-400",
      tooltip: "Change status",
    },
    {
      id: "jobDepartmentFeatured",
      icon: "fa-solid fa-star text-yellow-300",
      tooltip: "Change featured status",
    }
  ];

  function actionHandler(val) {
    switch (val) {
      case "jobDepartmentDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

    case "jobDepartmentStatus":
        setStatusModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

    case "jobDepartmentFeatured":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  return (
    <div>
      <div className="space-y-4 w-full flex flex-col pb-10">
        <div className="space-y-8">
          <div className="flex items-center gap-3">
            <TableControls
              limit={filters?.limit}
              totalDocs={data?.metadata?.totalDocs}
              onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
              onSearch={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} Selected
                  <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
            <div className="relative ml-auto">
              <Button
                buttonClasses={""}
                buttonLabel={"Add Department"}
                buttonIcon={"fa-regular fa-plus"}
                buttonIconPosition="left"
                buttonLabelClasses={"font-medium !text-sm"}
                buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
              />
            </div>
          </div>
        </div>

        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          totalDataCount={data?.metadata?.totalDocs}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
          selectable
        />

        <CategoryMangeDrawer
          open={modalOpen?.isOpen}
          data={modalOpen?.data}
          close={() => setModalOpen({ isOpen: false, data: null })}
          getResponse={taxonomyManageResponse}
          type={"department"}
          title={"Job Department"}
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />

        <StatusModal
          open={statusModal?.isOpen}
          loading={statusChangeInPending}
          onClose={() => setStatusModal((pre) => ({ data: null, isOpen: false }))}
          statusHandler={() => {
            statusChangeHandler(statusModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default JobDepartment;